import axios from 'axios';
import React, { useEffect, useState, useCallback } from 'react';
import { AlertTriangle } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend,
  PieChart, Pie, Cell,
} from 'recharts';
import { API } from '../../config';

const PCPL_COLOR = '#ef4444'; // red
const SOURCE_COLOR = '#f97316'; // orange
const RUN_COLOR = '#10b981'; // green

const TICK_SM = { fontSize: 11 };
const TICK_XS = { fontSize: 10 };
const LEGEND_STYLE = { fontSize: 12 };
const BAR_RADIUS_FLAT = [0, 0, 0, 0];
const BAR_RADIUS_TOP = [3, 3, 0, 0];

const fmtMin = (m) => Number(m || 0).toLocaleString('en-IN');

const StoppageView = ({ dashboardPlant, selectedDate, selectedYear, selectedMonth, token, initialFrom, initialTo }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const plantId = dashboardPlant?.id || 'all';

  // If a from/to range is passed in (e.g. when navigating from a Stoppage tile),
  // honor it. Otherwise default to the last 30 days.
  const today = new Date();
  const defaultFrom = initialFrom || new Date(today.getTime() - 29 * 86400000).toISOString().slice(0, 10);
  const defaultTo = initialTo || today.toISOString().slice(0, 10);
  const [fromDate, setFromDate] = useState(defaultFrom);
  const [toDate, setToDate] = useState(defaultTo);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await axios.get(
        `${API}/stoppage/${plantId}?from=${fromDate}&to=${toDate}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load stoppage data');
      setData(null);
    } finally { setLoading(false); }
  }, [plantId, fromDate, toDate, token]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex justify-center py-10"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#156ACA]" /></div>;
  if (error) return <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center text-gray-700">{error}</div>;
  if (!data) return null;

  const pieData = [
    { name: 'PCPL', value: data.totals.pcpl_mins },
    { name: 'Source', value: data.totals.source_mins },
  ];

  return (
    <div className="space-y-4" data-testid="stoppage-view">
      {/* Date range selector */}
      <div className="bg-white rounded-lg shadow-md p-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center space-x-2 flex-1 sm:flex-none min-w-0">
          <label className="text-sm font-medium text-gray-700 flex-shrink-0">From</label>
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
            className="flex-1 sm:flex-none min-w-0 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#156ACA] text-sm"
            data-testid="stoppage-from-input" />
        </div>
        <div className="flex items-center space-x-2 flex-1 sm:flex-none min-w-0">
          <label className="text-sm font-medium text-gray-700 flex-shrink-0">To</label>
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
            className="flex-1 sm:flex-none min-w-0 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#156ACA] text-sm"
            data-testid="stoppage-to-input" />
        </div>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3">
        <div className="bg-white rounded-lg shadow-md p-3 sm:p-4 border-l-4 border-emerald-500">
          <p className="text-[10px] sm:text-xs text-gray-500 uppercase tracking-wide">Running</p>
          <p className="text-xl sm:text-2xl font-bold text-gray-900">{fmtMin(data.totals.running_mins)}</p>
          <p className="text-[10px] sm:text-xs text-gray-500">minutes</p>
        </div>
        <div className="bg-white rounded-lg shadow-md p-3 sm:p-4 border-l-4 border-red-500">
          <p className="text-[10px] sm:text-xs text-gray-500 uppercase tracking-wide">PCPL Stoppage</p>
          <p className="text-xl sm:text-2xl font-bold text-gray-900">{fmtMin(data.totals.pcpl_mins)}</p>
          <p className="text-[10px] sm:text-xs text-gray-500">mins · {data.totals.pcpl_pct}%</p>
        </div>
        <div className="bg-white rounded-lg shadow-md p-3 sm:p-4 border-l-4 border-orange-500">
          <p className="text-[10px] sm:text-xs text-gray-500 uppercase tracking-wide">Source Stoppage</p>
          <p className="text-xl sm:text-2xl font-bold text-gray-900">{fmtMin(data.totals.source_mins)}</p>
          <p className="text-[10px] sm:text-xs text-gray-500">mins · {data.totals.source_pct}%</p>
        </div>
        <div className="bg-white rounded-lg shadow-md p-3 sm:p-4 border-l-4 border-gray-500">
          <p className="text-[10px] sm:text-xs text-gray-500 uppercase tracking-wide">Total Stoppage</p>
          <p className="text-xl sm:text-2xl font-bold text-gray-900">{fmtMin(data.totals.stoppage_mins)}</p>
          <p className="text-[10px] sm:text-xs text-gray-500">minutes</p>
        </div>
      </div>

      {/* Charts: stacked bar + donut */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow-md p-4 lg:col-span-2">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Daily Stoppage Minutes (PCPL vs Source)</h3>
          <div style={{ width: '100%', height: 280 }}>
            <ResponsiveContainer>
              <BarChart data={data.daily}>
                <XAxis dataKey="date" tick={TICK_XS} />
                <YAxis tick={TICK_SM} />
                <Tooltip />
                <Legend wrapperStyle={LEGEND_STYLE} />
                <Bar dataKey="pcpl_mins" stackId="a" fill={PCPL_COLOR} name="PCPL" radius={BAR_RADIUS_FLAT} />
                <Bar dataKey="source_mins" stackId="a" fill={SOURCE_COLOR} name="Source" radius={BAR_RADIUS_TOP} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Stoppage Split</h3>
          <div style={{ width: '100%', height: 280 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={90} dataKey="value" label>
                  <Cell fill={PCPL_COLOR} />
                  <Cell fill={SOURCE_COLOR} />
                </Pie>
                <Tooltip formatter={(v) => `${fmtMin(v)} min`} />
                <Legend wrapperStyle={LEGEND_STYLE} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Reasons log */}
      {data.reasons_log && data.reasons_log.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
            <AlertTriangle className="h-4 w-4 mr-2 text-amber-600" />
            Stoppage Reasons Log ({data.reasons_log.length})
          </h3>
          <div className="max-h-80 overflow-y-auto overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-700">
                <tr>
                  <th className="text-left px-3 py-2">Date</th>
                  <th className="text-left px-3 py-2">Shift</th>
                  <th className="text-right px-3 py-2">PCPL (m)</th>
                  <th className="text-right px-3 py-2">Source (m)</th>
                  <th className="text-left px-3 py-2">Reason</th>
                </tr>
              </thead>
              <tbody>
                {data.reasons_log.map((r) => (
                  <tr key={r.date + '-' + r.shift + '-' + r.reason} className="border-t border-gray-100">
                    <td className="px-3 py-2">{r.date}</td>
                    <td className="px-3 py-2">{r.shift}</td>
                    <td className="px-3 py-2 text-right text-red-700">{fmtMin(r.pcpl_mins)}</td>
                    <td className="px-3 py-2 text-right text-orange-700">{fmtMin(r.source_mins)}</td>
                    <td className="px-3 py-2 text-gray-700">{r.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default StoppageView;
